// hooks/useBulkCustomerUpload.js
import { useState, useEffect } from "react";
import Papa from "papaparse";
import CustomerService from "services/CustomerService";

export default function useBulkCustomerUpload() {
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [result, setResult] = useState(null); // { successful, failed, total, batchId }
    const [errorMessage, setErrorMessage] = useState("");

    // ------------------------------------------
    // PROGRESS ANIMATION (like useDealerInventoryUpload)
    // ------------------------------------------
    useEffect(() => {
        let timer;
        if (isUploading) {
            setProgress(0);
            timer = setInterval(() => {
                setProgress((prev) => {
                    if (prev < 85) return prev + 1;
                    if (prev < 98) return +(prev + 0.25).toFixed(2);
                    return prev;
                });
            }, 100);
        }
        return () => clearInterval(timer);
    }, [isUploading]);

    // ------------------------------------------
    // MAIN UPLOAD HANDLER
    // ------------------------------------------
    const handleUpload = (file, dealerId) => {
        setIsUploading(true);
        setErrorMessage("");
        setShowSuccess(false);
        setResult(null);

        // -------------------------------
        // 1. Parse CSV with PapaParse
        // -------------------------------
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (parsed) => {
                const rows = parsed.data;

                if (!Array.isArray(rows) || rows.length === 0) {
                    setIsUploading(false);
                    setErrorMessage("CSV file is empty or invalid.");
                    return;
                }

                try {
                    // -------------------------------
                    // 2. Send to backend
                    // -------------------------------
                    const response = await CustomerService.bulkImport({
                        dealerId,
                        rows,
                    });

                    // Force progress to 100%
                    setProgress(100);

                    // -------------------------------
                    // 3. Store result
                    // -------------------------------
                    setResult(response.data);
                    setShowSuccess(true);
                } catch (err) {
                    console.error("Bulk import failed", err);
                    setErrorMessage(
                        err?.response?.data?.message || err?.message || "Bulk import failed"
                    );
                } finally {
                    // Stop progress animation
                    setIsUploading(false);
                }
            },
            error: (err) => {
                console.error("CSV parse error", err);
                setIsUploading(false);
                setErrorMessage("Failed to read CSV file.");
            },
        });
    };

    return {
        isUploading,
        progress,
        showSuccess,
        setShowSuccess,
        result,
        errorMessage,
        handleUpload,
    };
}
